<script setup>
import { ref } from "vue";
import { RouterLink } from "vue-router";
import { useModals } from "@/composables/useModals";
import { useRequestsStore } from "@/stores/storeRequests";
import { getPromoCodeErrorMessage, savePendingPromoCode } from "@/utils/promoCode";
import { setParkGorkogoEventRegistrationParam } from "@/composables/useParkGorkogoEventRegistration";

const props = defineProps({
	requireAuthOnFocus: {
		type: Boolean,
		default: false,
	},
	showBrandLink: {
		type: Boolean,
		default: true,
	},
	placeholder: {
		type: String,
		default: "Введи промокод",
	},
});

const { openModal } = useModals();
const storeRequests = useRequestsStore();

const promoCode = ref("");
const isLoading = ref(false);

const openPromoRegistrationRequiredModal = () => {
	if (props.requireAuthOnFocus) {
		setParkGorkogoEventRegistrationParam();
	}

	openModal("promoCodeRegistrationRequired", { size: "w400" });
};

const submitPromoCode = async () => {
	const trimmedCode = promoCode.value.trim();

	if (!trimmedCode || isLoading.value) {
		return;
	}

	if (!storeRequests.isAuthenticated) {
		savePendingPromoCode(trimmedCode);
		promoCode.value = "";
		openPromoRegistrationRequiredModal();
		return;
	}

	isLoading.value = true;

	try {
		const data = await storeRequests.activatePromoCode(trimmedCode);
		promoCode.value = "";
		openModal("promoCodeSuccess", {
			content: { points: data.points_awarded },
			size: "w400",
		});
	} catch (error) {
		openModal("promoCodeError", {
			content: { message: getPromoCodeErrorMessage(error) },
			size: "w400",
		});
	} finally {
		isLoading.value = false;
	}
};
</script>

<template>
	<div class="promo-code-block">
		<div class="promo-code-block__inner">
			<div class="promo-code-block__content">
				<p class="promo-code-block__title">
					Введите промокод с листовки,
					<br>полученной в&nbsp;бренд зоне,
					<br>и получите дополнительные
					<span>
						50&nbsp;<svg width="24" height="24" class="promo-code-block__leaf" aria-hidden="true">
							<use href="#leaf"></use>
						</svg>
					</span>
				</p>

				<ul class="promo-code-block__list">
					<li class="promo-code-block__list-item">1 пользователь может ввести не более одного промокода</li>
					<li class="promo-code-block__list-item">Купон с промокодом выдается за покупку 2 и более&nbsp;пачек чая</li>
				</ul>

				<form class="promo-code-block__form" @submit.prevent="submitPromoCode">
					<label class="promo-code-block__field">
						<input
							v-model="promoCode"
							class="promo-code-block__input"
							type="text"
							:placeholder="placeholder"
							autocomplete="off"
							aria-label="Промокод"
							:disabled="isLoading"
						/>
						<button
							class="promo-code-block__submit"
							type="submit"
							:disabled="isLoading || !promoCode.trim()"
							aria-label="Активировать промокод"
						>
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
								<path d="M20.7806 12.531L14.0306 19.281C13.8899 19.4218 13.699 19.5008 13.5 19.5008C13.301 19.5008 13.1101 19.4218 12.9694 19.281C12.8286 19.1403 12.7496 18.9494 12.7496 18.7504C12.7496 18.5514 12.8286 18.3605 12.9694 18.2198L18.4397 12.7504H3.75C3.55109 12.7504 3.36032 12.6714 3.21967 12.5307C3.07902 12.3901 3 12.1993 3 12.0004C3 11.8015 3.07902 11.6107 3.21967 11.4701C3.36032 11.3294 3.55109 11.2504 3.75 11.2504H18.4397L12.9694 5.78104C12.8286 5.64031 12.7496 5.44944 12.7496 5.25042C12.7496 5.05139 12.8286 4.86052 12.9694 4.71979C13.1101 4.57906 13.301 4.5 13.5 4.5C13.699 4.5 13.8899 4.57906 14.0306 4.71979L20.7806 11.4698C20.8504 11.5394 20.9057 11.6222 20.9434 11.7132C20.9812 11.8043 21.0006 11.9019 21.0006 12.0004C21.0006 12.099 20.9812 12.1966 20.9434 12.2876C20.9057 12.3787 20.8504 12.4614 20.7806 12.531Z" fill="#FFD08E"/>
							</svg>
						</button>
					</label>
				</form>

				<RouterLink
					v-if="showBrandLink"
					to="/parkgorkogo/"
					class="promo-code-block__link"
				>
					Подробнее о бренд-зоне
				</RouterLink>
			</div>

			<div class="promo-code-block__visual">
				<img
					src="@/assets/img/main/promo-code.png"
					srcset="@/assets/img/main/promo-code@2x.png 2x"
					class="promo-code-block__image promo-code-block__image--desktop"
					width="512"
					height="400"
					alt=""
				>
				<img
					src="@/assets/img/main/promo-code-mobile.png"
					srcset="@/assets/img/main/promo-code-mobile@2x.png 2x"
					class="promo-code-block__image promo-code-block__image--mobile"
					width="360"
					height="240"
					alt=""
				>

				<img
					src="@/assets/img/main/promo-mini.png"
					srcset="@/assets/img/main/promo-mini@2x.png 2x"
					class="promo-code-block__mini"
					alt=""
				>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/base/vars";


.park-gorkogo-banners {
	display: flex;
	flex-direction: column;
	gap: 2rem;
	padding-top: 2.5rem;
	padding-bottom: 2.5rem;

	&__promo {
		.promo-code-block__content {
			padding: 4.5rem 9.6rem 0 4.8rem;

			@Media (max-width: 992px) {
				padding: 0 1.6rem 1.6rem 1.6rem;
			}
		}
		.promo-code-block__visual {
			width: calc(50% + 7rem);

			@Media (max-width: 992px) {
				width: 100%;
			}
		}
		.promo-code-block__title {
			font-family: var(--font-family);
			font-weight: 500;
			font-size: 2rem;
			line-height: 1.2;
			color: var(--text-accent-2-100);

			br {
				display: none;
			}

			@Media (max-width: 992px) {
				font-size: 1.6rem;
				text-align: justify;
			}
		}
	}

	.promo-code-block__mini {
		@media (max-width: 992px) {
			display: none !important;
		}
	}
}
.promo-code-block {

	&__mini {
		display: none;
		.park-gorkogo-banners & {
			display: block;
		}
	}

	@extend .base-bg-light;
	border: 1px solid var(--border-accent-2-3);
	border-radius: 4rem;
	backdrop-filter: blur(15.399999618530273px);
	box-shadow: inset 0 13px 35px 0 rgba(255, 255, 255, 0.14), 0 16px 33px 0 rgba(0, 0, 0, 0.25);
	background: linear-gradient(143deg, #165230 0%, #0b2e1b 100%);
	overflow: hidden;

	@media (max-width: vars.$x480) {
		border-radius: 2.4rem;
	}

	&__inner {
		display: flex;

		@media (max-width: 767px) {
			flex-direction: column-reverse;
		}
	}

	&__visual {
		width: 50%;

		@media (max-width: 767px) {
			width: 100%;
		}
	}

	&__image {
		object-fit: cover;
		object-position: left top;
		width: 100%;
		height: 100%;

		@media (min-width: 993px) {
			.park-gorkogo-banners & {
				display: none !important;
			}
		}

		&--mobile {
			@media (min-width: 768px) {
				display: none;
			}
		}

		&--desktop {
			@media (max-width: 767px) {
				display: none;
			}
		}
	}

	&__content {
		padding: 4.7rem 4rem 3.2rem 4rem;
		display: flex;
		flex-direction: column;
		gap: 2.4rem;
		width: 50%;

		@media (max-width: 767px) {
			width: 100%;
		}

		@media (max-width: vars.$x480) {
			padding: 0 2.4rem 3rem 2.4rem;
		}
	}

	&__title {
		font-family: var(--font-family);
		font-weight: 500;
		font-size: 2rem;
		line-height: 1.2;
		color: var(--text-inverse-100);

		span {
			white-space: nowrap;
		}

		@media (max-width: 767px) {
			font-size: 1.6rem;

			br {
				display: none;
			}
		}
	}

	&__leaf {
		display: inline-block;
		width: 2.4rem;
		height: 2.4rem;
		vertical-align: middle;
		margin-left: 0;
		color: var(--text-accent-2-100);
		position: relative;
		top: -.2rem;
	}

	&__list {
		display: flex;
		flex-direction: column;
		gap: 0.8rem;
		margin: 0;
		padding: 0;
		list-style: none;
	}

	&__list-item {
		position: relative;
		padding-left: 1.6rem;
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 1.2;
		color: var(--text-inverse-100);

		&::before {
			content: "•";
			position: absolute;
			left: 0;
			color: var(--text-accent-2-100);
		}

		@media (max-width: 767px) {
			font-size: 1.4rem;
		}
	}

	&__form {
		width: 100%;
	}

	&__field {
		display: flex;
		align-items: stretch;
		width: 100%;
		border: 1px solid var(--border-accent-2-3);
		border-radius: 1.6rem;
		overflow: hidden;
		background: rgba(0, 0, 0, 0.2);
	}

	&__input {
		flex: 1;
		min-width: 0;
		padding: 0 2rem;
		height: 5.6rem;
		border: none;
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 1.4;
		color: var(--text-inverse-100);
		background: var(--fill-accent-1-1);

		&::placeholder {
			color: rgba(255, 255, 255, 0.5);
		}

		&:focus {
			outline: none;
		}

		&:disabled {
			opacity: 0.6;
		}
	}

	&__submit {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 5.6rem;
		border: none;
		transition: opacity var(--transition);
		background: #0e2e1b;

		@media (hover: hover) {
			&:hover:not(:disabled) {
				opacity: 0.9;
			}
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}

		svg {
			width: 2.4rem;
			height: 2.4rem;
		}
	}

	&__link {
		width: fit-content;
		padding: 0;
		border: none;
		background: transparent;
		font-weight: 500;
		font-size: 1.6rem;
		line-height: 1.4;
		text-decoration: underline;
		color: var(--text-accent-2-100);
		cursor: pointer;
		text-decoration-skip-ink: none;

		@media (hover: hover) {
			&:hover {
				text-decoration: none;
			}
		}

		@media (max-width: 767px) {
			display: block;
			text-align: center;
			width: 100%;
		}
	}
}
</style>
